import { BitmapCoordinatesRenderingScope } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    LineTypes,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { Coordinate, PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { scaledCoordinate } from '../drawing.helper'
import { primaryDrawingColor } from '../../../../assets/colors'

export type VerticalLineDrawingState = {
    p1?: Point
}

export type VerticalLineDrawingSettings = {
    lineType: LineTypes,
    lineColor: ColorValue,
    lineWidth: number,
    point1: Point
}

const THRESHOLD = 10 // cursor distance threshold for treating as hovered or not

class VerticalLineTool extends DrawingBase<any, VerticalLineDrawingSettings, VerticalLineDrawingState> {
    scaledCoordinates = {
        p1x: 0,
        p1y: 0
    }

    draw(scope: BitmapCoordinatesRenderingScope) {
        const ctx = scope.context
        this.calculateLineAttributes()
        let start = this.getStartCoordinate()
        let end = this.getEndCoordinate()
        if (!start || !end || start.x === 0) return

        ctx.beginPath()
        this.setLineAttributes(ctx)
        ctx.moveTo(start.x, start.y)
        ctx.lineTo(end.x, end.y)
        ctx.stroke()
        ctx.closePath()
    };

    calculateLineAttributes() {
        const { p1 } = this._state
        if (!p1) return
        let p1x = this.timeToCoordinate(p1.x)
        let p1y = this.priceToCoordinate(p1.y)
        if (p1x && p1y) {
            p1x = scaledCoordinate(p1x, this.horizontalPixelRatio) as Coordinate
            p1y = scaledCoordinate(p1y, this.verticalPixelRatio) as Coordinate
            this.scaledCoordinates = { p1x, p1y }
        }
    }

    setLineAttributes(ctx: CanvasRenderingContext2D, setLineType = true) {
        let lineDash: number[] = []
        const lineWidth = this._settings.lineWidth * window.devicePixelRatio
        if (this._settings.lineType === 'dashed') {
            lineDash = [Math.max(lineWidth * 3, 8), 3]
        } else if (this._settings.lineType === 'dotted') {
            lineDash = [lineWidth, lineWidth]
        }
        setLineType && ctx.setLineDash(lineDash)
        ctx.globalAlpha = this._settings.lineColor.opacity
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = this._settings.lineColor.color
    }

    getStartCoordinate(): PointCoordinates | void {
        const { p1x } = this.scaledCoordinates
        return {
            x: p1x,
            y: 0
        }
    }

    getEndCoordinate(): PointCoordinates | void {
        let { p1x } = this.scaledCoordinates
        return { x: p1x, y: this.canvasHeight }
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const { p1 } = this._state
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!p1) {
            this._state.p1 = point
            !preview && this.onDrawingEnd()
        }
        if (!preview) return { ...point }
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1 } = this._state
        if (!p1) return []

        const p1x = this.timeToCoordinate(p1.x)
        if (p1x === undefined || p1x === null) return []

        return [{
            id: 'time',
            p1: p1.x,
            p2: p1.x,
            fill: true,
            fillColor: this._settings.lineColor.color,
            labelColors: {
                p1: this._settings.lineColor.color,
                p2: this._settings.lineColor.color
            }
        }]
    }

    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1 } = this._state
        if (p1) {
            handles.push(p1)
        }
        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const lineStart = this.getStartCoordinate()
        const lineEnd = this.getEndCoordinate()

        if (!lineStart || !lineEnd) {
            return null
        }

        const lineX = lineStart.x // Assuming x-coordinate represents the vertical line
        const xPoint = lineX ?? 0

        // Adjust threshold check for vertical line
        if (scaledX < xPoint - THRESHOLD / 3 || scaledX > xPoint + THRESHOLD / 3) {
            return null
        }

        const startY = Math.min(lineStart.y, lineEnd.y)
        if (scaledY < startY) {
            return null
        }
        const endY = Math.max(lineStart.y, lineEnd.y)
        if (scaledY > endY) {
            return null
        }

        return {
            externalId: this.id,
            zOrder: 'normal'
        }
    }

    updateHandle = (id: string, time: Time, price: number) => {
        const { p1 } = this._state
        if (!p1) return
        const p1y = p1.y
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = p1y
                break
        }
    }

    onSettingsUpdate = (settings: Partial<VerticalLineDrawingSettings>) => {
        if (settings.point1 && this._state.p1) {
            this._state.p1 = settings.point1
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<VerticalLineDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'lineColor',
                    label: 'Line Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineType',
                    label: 'Line Type',
                    type: SettingType.LINE_TYPE,
                    defaultValue: 'normal',
                    custom: true,
                    visible: true
                },
                {
                    name: 'lineWidth',
                    label: 'Line Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: '#1 (bar)',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true,
                    fieldOptions: {
                        showX: true,
                        showY: false
                    }
                }
            ]
        }]
    }

    getDrawingType(): DrawingTypes {
        return 'verticalLine'
    }

    getSettingsValues(): VerticalLineDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        return settings
    }

    getMiniSettings = () => {
        return ['lineColor', 'lineType', 'lineWidth'] as (keyof VerticalLineDrawingSettings)[]
    }
}

export default VerticalLineTool